import * as React from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import styled from 'styled-components';
import TagManager from 'react-gtm-module';
import AppBar from '@mybet/components/AppBar';
import ThemeProvider from '@mybet/components/theme/ThemeProvider';
import AppFooter, { AppFooterProps } from '@mybet/core/blocks/AppFooter';
import CampaignPage from '@mybet/core/blocks/CampaignPage';
import { Link as LinkType } from '@mybet/core/libs/apollo/cache';
import getLandingPage from '@mybet/core/libs/contentful/pages/getLandingPage';
import getLocalisation from '@mybet/core/libs/contentful/localization';
import { Domain } from '@mybet/core/libs/types';
import Logo from '../../assets/mybet_logo.svg';
import { client } from '../../contenfulInstance';

const footerConfig = {
  certificateIcons: [
    { icon: 'comodo', href: 'https://ssl.comodo.com/' },
    { icon: 'pci' },
    { icon: 'gamecare', href: 'https://www.gamcare.org.uk/' },
    { icon: '18plus' },
  ],
  paymentIcons: [],
} as Pick<
  AppFooterProps,
  'certificateIcons' | 'thirdPartyIcons' | 'paymentIcons'
>;

const baseUrl = `${process.env.REDIRECT}/de`;
const footerLinks = [{ id: 'sport', href: `${baseUrl}/sport#home` }];

if (process.env.BRAND === 'mybet.com') {
  footerConfig.paymentIcons.push(
    'visa_large',
    'mastercard_large',
    'paysafe_large',
    'sofort_large',
    'trustly_large'
  );
  footerConfig.certificateIcons.push({
    icon: 'mga',
    href:
      'https://www.authorisation.mga.org.mt/verification.aspx?lang=EN&company=c7ae72e5-c340-4670-bf3b-96bc87119d38&details=1',
  });
  footerConfig.thirdPartyIcons = [
    'netent',
    'quickspin',
    'playngo',
    'gamomat',
    'microgaming',
    'scientificgames',
    'nolimitcity',
    'stakelogic',
    'lega',
    'yggdrasil',
    'playson',
    'nyxgaming',
  ];
  footerLinks.push(
    { id: 'casino', href: `${baseUrl}/casino-games` },
    { id: 'liveCasino', href: `${baseUrl}/live-casino` },
    { id: 'virtualSports', href: `${baseUrl}/virtual-sports` }
  );
} else {
  footerConfig.paymentIcons.push('paypal_large');
}

const MybetLogo = styled.div<{ src: string }>(({ theme, src }) => ({
  backgroundImage: `url("${src}")`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  height: 22,
  width: 68,

  [theme.breakpoints.up('md')]: {
    height: 32,
    width: 102,
  },
}));

const ContentRoot = styled.div((props) => ({
  minHeight: '95vh',
  backgroundColor: props.theme.blue14,
}));

const Link = styled.a({});

const App: React.FC = () => {
  const params = new URLSearchParams(location.search);
  const affToken = params.get('affToken');
  const affType = params.get('type');
  const affRedirect =
    affToken && `affToken=${affToken}&type=${affType ? affType : 'AF'}`;
  const [content, setContent] = React.useState<any>();
  const [t, setTranslations] = React.useState<Record<string, string>>({});
  const [infoLinks, setInfoLinks] = React.useState<LinkType[]>([]);
  const [isCasinoPage, setIsCasinoPage] = React.useState(
    window.location.pathname.split('/').slice(-1)[0].toLowerCase() === 'casino'
  );

  React.useEffect(() => {
    (async function getData() {
      try {
        const [translations, pageConfig] = await Promise.all([
          getLocalisation(
            client,
            'de',
            'common',
            process.env.DOMAIN === 'mybet.de' ? 'mybet.com' : 'mybet.de' // ignore domain
          ),
          getLandingPage(client, process.env.DOMAIN as Domain, 'de'),
        ]);

        setTranslations(translations);
        setContent(pageConfig.content);
        setInfoLinks(
          pageConfig.footerPageLinks.map((page: any) => ({
            ...page,
            href: `${baseUrl}/info/${page.id}`,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    })();
  }, [isCasinoPage]);

  React.useEffect(() => {
    setIsCasinoPage(
      window.location.pathname.split('/').slice(-1)[0].toLowerCase() ===
        'casino'
    );

    // Initiating Google Tag Manager
    if (process.env.INITIALISE_GTM === 'true') {
      const hostnameItems = window.location.hostname.split('.');
      TagManager.initialize({
        gtmId: process.env.GTM_PROPERTY_ID as string,
        dataLayerName: 'dataLayer',
      });
      TagManager.dataLayer({
        dataLayerName: 'dataLayer',
        dataLayer: {
          event: 'PageView',
          path: `/${hostnameItems[hostnameItems.length - 1]}/landing`, // e.g  '/com/landing' or '/de/landing'
        },
      });
    }
  }, []);

  const handleButtonClick = React.useCallback(() => {
    window.location.href = `${process.env.REDIRECT}/de/sport${
      affRedirect ? `?${affRedirect}&` : '?'
    }registration=true`;
  }, [affRedirect]);

  const handleCasinoGameClick = React.useCallback(
    (gameSlug: string) => {
      window.location.href = `${
        process.env.REDIRECT
      }/de/casino-games/game/${encodeURIComponent(gameSlug)}${
        affRedirect ? `?${affRedirect}` : ''
      }`;
    },
    [affRedirect]
  );

  const handleBannerClick = React.useCallback(() => {
    window.location.href = `${process.env.REDIRECT}/de/sport${
      affRedirect ? `?${affRedirect}&` : '?'
    }modal=euro2021-raffle`;
  }, [affRedirect]);

  return (
    <Router>
      <ThemeProvider>
        <AppBar>
          <a
            href={`${process.env.REDIRECT}/de/sport`}
            style={{ margin: '0 auto' }}
          >
            <MybetLogo src={Logo} />
          </a>
        </AppBar>
        <Switch>
          {process.env.ENABLE_CASINO === 'true' && (
            <Route exact path="/de/casino" key={'casino'}>
              <ContentRoot>
                {content && (
                  <CampaignPage
                    pageConfig={content.pageConfig.config}
                    onButtonClick={handleButtonClick}
                    onCasinoGameClick={handleCasinoGameClick}
                    onBannerClick={handleBannerClick}
                    ImageProps={{ unoptimized: true }}
                  />
                )}
              </ContentRoot>
            </Route>
          )}
          <Route exact path={['/', '/de']} key={'sport'}>
            <ContentRoot>
              {content && (
                <CampaignPage
                  pageConfig={content.pageConfig.config}
                  onButtonClick={handleButtonClick}
                  onCasinoGameClick={handleCasinoGameClick}
                  onBannerClick={handleBannerClick}
                  ImageProps={{ unoptimized: true }}
                />
              )}
            </ContentRoot>
          </Route>
          <Redirect to={'/'} />
        </Switch>

        <AppFooter
          {...footerConfig}
          homeLink={`${baseUrl}/de/sport#home`}
          Link={Link}
          translations={t}
          links={footerLinks}
          infoLinks={infoLinks}
        />
      </ThemeProvider>
    </Router>
  );
};

export default App;
